import { useState } from 'react';
import api from '../lib/api';

/**
 * FollowButton — single source of truth for follow UX.
 *
 * States:
 *   - "Following"   → I currently follow them (is_following === 'active')
 *   - "Requested"   → I sent a request awaiting approval (is_following === 'pending')
 *   - "Follow Back" → They follow me but I don't follow them (!is_following && follows_me)
 *   - "Follow"      → No relationship in either direction
 *
 * Props:
 *   user: object with { id, is_following, follows_me, ... }
 *   onChange?: (newIsFollowing: 'active'|'pending'|null) => void
 *   size?: 'sm' | 'md'  (sm for rows, md for profile header)
 *   variant?: 'default' | 'block'  (block = fills available width)
 *   testIdPrefix?: string
 */
export default function FollowButton({
  user,
  onChange,
  size = 'sm',
  variant = 'default',
  testIdPrefix = 'follow',
}) {
  const [loading, setLoading] = useState(false);
  // Normalize: backend sometimes returns boolean (followers/following lists), sometimes string status.
  const normalize = (v) => {
    if (v === true) return 'active';
    if (v === false) return null;
    return v ?? null; // 'active' | 'pending' | null
  };
  const [localStatus, setLocalStatus] = useState(normalize(user?.is_following));
  // Follow-back stays correct even after click (backend re-fetch not required for UX).
  const followsMe = !!user?.follows_me;

  const status = localStatus; // 'active' | 'pending' | null

  const handleClick = async (e) => {
    e.stopPropagation();
    if (loading || !user?.id) return;
    setLoading(true);
    try {
      if (status === 'active' || status === 'pending') {
        await api.delete(`/users/${user.id}/follow`);
        setLocalStatus(null);
        onChange?.(null);
      } else {
        const { data } = await api.post(`/users/${user.id}/follow`);
        const newStatus = data?.status || 'active';
        setLocalStatus(newStatus);
        onChange?.(newStatus);
      }
    } catch {
      // silent — keep UI in sync with current localStatus
    }
    setLoading(false);
  };

  const { label, style, testId } = computeDisplay(status, followsMe, testIdPrefix);

  const sizeCls =
    size === 'md'
      ? 'py-2.5 text-sm'
      : 'px-4 py-1.5 text-xs';
  const widthCls = variant === 'block' ? 'flex-1' : 'flex-shrink-0';

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={handleClick}
      disabled={loading}
      className={`rounded-full font-bold active:scale-95 transition-transform ${widthCls} ${sizeCls} ${style} ${loading ? 'opacity-60' : ''}`}
    >
      {loading ? '…' : label}
    </button>
  );
}

function computeDisplay(status, followsMe, prefix) {
  if (status === 'active') {
    return {
      label: 'Following',
      style: 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.08]',
      testId: `${prefix}-following`,
    };
  }
  if (status === 'pending') {
    return {
      label: 'Requested',
      style: 'bg-[#F1F0EA] text-[#9B9EA3] border border-black/[0.06]',
      testId: `${prefix}-requested`,
    };
  }
  if (followsMe) {
    return {
      label: 'Follow Back',
      style: 'bg-[#B6E600] text-[#111111]',
      testId: `${prefix}-follow-back`,
    };
  }
  // Default Follow — ASAP lime brand primary. Reinforces brand and reads as
  // the page's primary action when paired with the secondary white Message
  // button.
  return {
    label: 'Follow',
    style: 'bg-[#B6E600] text-[#111111]',
    testId: `${prefix}-follow`,
  };
}
